// Admin "Create user with full profile" — single-page form that
// wires up every part of the admin user-builder backend:
//   • auth shell + force-verify email
//   • bulk photo upload (presigned, NSFW bypassed)
//   • bulk video upload (ffmpeg transcode + dual presign)
//   • profile patch (bio, prompts, vices, themeSong, location)
//   • DeepSeek-backed Generate button (faker fallback)
//   • publish on behalf
//
// Trust model: every backend call is admin-token-gated; nothing
// flows through the public client.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAction, useQuery } from 'convex/react';
import { fakerEN_US as faker } from '@faker-js/faker';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { api as convexApi } from '../../../convex/functions/_generated/api';
import { friendlyError } from '@/lib/errors';
import {
  builderAPI,
  BuilderError,
  type Location,
  type ProfilePatch,
  type ThemeSong,
} from '@/lib/profileBuilderApi';

type Gender = 'woman' | 'man';
type LookingFor = 'men' | 'women' | 'everyone';
type EducationLevel = 'high_school' | 'undergraduate' | 'postgraduate' | 'prefer_not_to_say';
type Politics = 'not_political' | 'moderate' | 'liberal' | 'conservative' | 'other' | 'prefer_not_to_say';
type DatingIntention =
  | 'life_partner'
  | 'long_term'
  | 'long_term_open_to_short'
  | 'short_term_open_to_long'
  | 'short_term'
  | 'figuring_out';

const DATING_INTENTIONS: Array<{ value: DatingIntention; label: string }> = [
  { value: 'life_partner', label: 'Life partner' },
  { value: 'long_term', label: 'Long-term' },
  { value: 'long_term_open_to_short', label: 'Long-term, open to short' },
  { value: 'short_term_open_to_long', label: 'Short-term, open to long' },
  { value: 'short_term', label: 'Short-term' },
  { value: 'figuring_out', label: 'Figuring it out' },
];

const VICE_PRESETS = ['drinks', 'smokes', '420', 'caffeine'];

const EDUCATION_LEVELS: Array<{ value: EducationLevel; label: string }> = [
  { value: 'high_school', label: 'High school' },
  { value: 'undergraduate', label: 'Undergraduate' },
  { value: 'postgraduate', label: 'Postgraduate' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

const POLITICS: Array<{ value: Politics; label: string }> = [
  { value: 'not_political', label: 'Not political' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'liberal', label: 'Liberal' },
  { value: 'conservative', label: 'Conservative' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

const RELIGIONS = [
  'Agnostic',
  'Atheist',
  'Buddhist',
  'Christian',
  'Hindu',
  'Jewish',
  'Muslim',
  'Spiritual',
  'Other',
  'Prefer not to say',
];

// ─── Component ─────────────────────────────────────────────────────────────

export function UsersNewPage() {
  const navigate = useNavigate();

  // ── Auth section ──
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [userId, setUserId] = useState<string | null>(null);
  const [authBusy, setAuthBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // ── Profile fields ──
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState<Gender | ''>('');
  const [lookingFor, setLookingFor] = useState<LookingFor[]>([]);
  const [bio, setBio] = useState('');
  const [education, setEducation] = useState('');
  const [educationLevel, setEducationLevel] = useState<EducationLevel | ''>('');
  const [job, setJob] = useState('');
  const [hometown, setHometown] = useState('');
  const [religion, setReligion] = useState('');
  const [politics, setPolitics] = useState<Politics | ''>('');
  const [languages, setLanguages] = useState<string[]>([]);
  const [languageInput, setLanguageInput] = useState('');
  const [vices, setVices] = useState<string[]>([]);
  const [viceInput, setViceInput] = useState('');
  const [datingIntentions, setDatingIntentions] = useState<DatingIntention[]>([]);
  const [promptAnswers, setPromptAnswers] = useState<
    Array<{ promptId: string; answer: string }>
  >([]);
  const [themeSong, setThemeSong] = useState<ThemeSong | null>(null);
  const [location, setLocation] = useState<Location | null>(null);

  // ── Photos / videos ──
  type PhotoRow = {
    id: string;           // local UUID (stable for React + dnd-kit keys)
    mediaId?: string;     // backend profileMedia id, set once upload finalizes
    file: File;
    state: 'uploading' | 'done' | 'error';
    error?: string;
    pct?: number;
  };
  const [photos, setPhotos] = useState<PhotoRow[]>([]);

  type VideoRow = {
    id: string;
    file: File;
    stage: 'probe' | 'transcode' | 'upload' | 'finalize' | 'done' | 'error';
    pct?: number;
    error?: string;
  };
  const [videos, setVideos] = useState<VideoRow[]>([]);

  // ── Misc ──
  const [autoPublish, setAutoPublish] = useState(true);
  const [saving, setSaving] = useState(false);
  const [published, setPublished] = useState<{ slug: string; public_url: string } | null>(
    null,
  );
  const [generating, setGenerating] = useState(false);

  const prompts = useQuery(convexApi.prompts.listActive, {});
  const searchTracks = useAction(convexApi.spotify.searchTracks);
  const forwardSearch = useAction(convexApi.geocoding.forwardSearchStructured);

  const promptsByCategory = useMemo(() => {
    const out: Record<string, Array<{ id: string; text: string }>> = {};
    for (const p of prompts ?? []) {
      (out[p.category] ??= []).push({ id: p.id, text: p.text });
    }
    return out;
  }, [prompts]);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const createUser = useCallback(async () => {
    if (authBusy) return;
    setAuthBusy(true);
    setErr(null);
    try {
      const r = await builderAPI.createUser({
        email: email.trim().toLowerCase(),
        password,
        display_name: displayName.trim() || undefined,
      });
      setUserId(r.user_id);
    } catch (e) {
      setErr(friendlyErrorOrCode(e));
    } finally {
      setAuthBusy(false);
    }
  }, [authBusy, email, password, displayName]);

  const onPhotosPicked = useCallback(
    async (files: FileList | null) => {
      if (!files || !userId) return;
      const next: PhotoRow[] = Array.from(files).map((file) => ({
        id: crypto.randomUUID(),
        file,
        state: 'uploading',
        pct: 0,
      }));
      setPhotos((prev) => [...prev, ...next]);
      for (const row of next) {
        try {
          const r = await builderAPI.uploadPhoto(userId, row.file, (pct) =>
            setPhotos((prev) =>
              prev.map((p) => (p.id === row.id ? { ...p, pct } : p)),
            ),
          );
          setPhotos((prev) =>
            prev.map((p) =>
              p.id === row.id
                ? { ...p, state: 'done', pct: 100, mediaId: r.id }
                : p,
            ),
          );
        } catch (e) {
          setPhotos((prev) =>
            prev.map((p) =>
              p.id === row.id
                ? { ...p, state: 'error', error: friendlyErrorOrCode(e) }
                : p,
            ),
          );
        }
      }
    },
    [userId],
  );

  // Drag-to-reorder via @dnd-kit. Position 1 = lead photo. We
  // reorder the local state optimistically and fire the server
  // call in the background; on failure we revert.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onPhotoDragEnd = useCallback(
    (e: DragEndEvent) => {
      const { active, over } = e;
      if (!over || active.id === over.id || !userId) return;
      setPhotos((prev) => {
        const from = prev.findIndex((p) => p.id === active.id);
        const to = prev.findIndex((p) => p.id === over.id);
        if (from < 0 || to < 0) return prev;
        const next = arrayMove(prev, from, to);
        // Fire the server reorder for the rows that have a
        // mediaId. Photos still uploading are kept in local order
        // but excluded from the server payload — they'll get
        // their final position from the server when the next
        // reorder runs (or implicit through their insert order).
        const orderedMediaIds = next
          .map((p) => p.mediaId)
          .filter((id): id is string => typeof id === 'string');
        if (orderedMediaIds.length > 0) {
          void builderAPI.reorderPhotos(userId, orderedMediaIds).catch((err) => {
            console.warn('[admin] photo reorder failed; reverting', err);
            setErr(friendlyErrorOrCode(err));
            setPhotos(prev); // revert local order
          });
        }
        return next;
      });
    },
    [userId],
  );

  const onVideosPicked = useCallback(
    async (files: FileList | null) => {
      if (!files || !userId) return;
      const next: VideoRow[] = Array.from(files).map((file) => ({
        id: crypto.randomUUID(),
        file,
        stage: 'probe',
      }));
      setVideos((prev) => [...prev, ...next]);
      for (const row of next) {
        try {
          await builderAPI.uploadVideo(userId, row.file, (stage, pct) => {
            setVideos((prev) =>
              prev.map((v) =>
                v.id === row.id ? { ...v, stage, pct } : v,
              ),
            );
          });
          setVideos((prev) =>
            prev.map((v) =>
              v.id === row.id ? { ...v, stage: 'done', pct: 100 } : v,
            ),
          );
        } catch (e) {
          setVideos((prev) =>
            prev.map((v) =>
              v.id === row.id
                ? { ...v, stage: 'error', error: friendlyErrorOrCode(e) }
                : v,
            ),
          );
        }
      }
    },
    [userId],
  );

  // ── Generate (faker structured + DeepSeek bio/answers + faker location) ──
  const generateAll = useCallback(async () => {
    if (generating || !gender) return;
    setGenerating(true);
    setErr(null);
    try {
      // 1. Faker structured fields (gender-aware where it matters).
      const fakerJob = faker.person.jobTitle();
      const fakerEducation = faker.helpers.arrayElement([
        'BA Literature',
        'BSc Computer Science',
        'MFA Creative Writing',
        'BA Psychology',
        'MEng',
        'Self-taught',
      ]);
      const fakerEduLevel = faker.helpers.arrayElement(
        EDUCATION_LEVELS.filter((e) => e.value !== 'prefer_not_to_say').map(
          (e) => e.value,
        ),
      );
      const fakerHometown = `${faker.location.city()}, ${faker.location.country()}`;
      const fakerReligion = faker.helpers.arrayElement(RELIGIONS);
      const fakerPolitics = faker.helpers.arrayElement(
        POLITICS.filter((p) => p.value !== 'prefer_not_to_say').map(
          (p) => p.value,
        ),
      );
      const fakerLanguages = faker.helpers
        .arrayElements(
          ['English', 'Spanish', 'French', 'German', 'Italian', 'Mandarin', 'Japanese', 'Portuguese'],
          { min: 1, max: 3 },
        );
      const fakerVices = faker.helpers.arrayElements(VICE_PRESETS, {
        min: 0,
        max: 2,
      });
      const fakerIntentions = faker.helpers.arrayElements(
        DATING_INTENTIONS.map((d) => d.value),
        { min: 1, max: 2 },
      );

      setJob(fakerJob);
      setEducation(fakerEducation);
      setEducationLevel(fakerEduLevel);
      setHometown(fakerHometown);
      setReligion(fakerReligion);
      setPolitics(fakerPolitics);
      setLanguages(fakerLanguages);
      setVices(fakerVices);
      setDatingIntentions(fakerIntentions);

      // 2. Pick 3 random prompts from the active catalog.
      const flatPrompts = Object.values(promptsByCategory).flat();
      const pickedPrompts =
        flatPrompts.length > 0
          ? faker.helpers.arrayElements(flatPrompts, { min: 3, max: 3 })
          : [];

      // 3. DeepSeek for bio + prompt answers (faker fallback).
      let generatedBio = '';
      let generatedAnswers: Array<{ promptId: string; answer: string }> = [];
      if (pickedPrompts.length > 0) {
        try {
          const r = await builderAPI.generateContent({
            gender,
            looking_for: lookingFor.length > 0 ? lookingFor : ['everyone'],
            dating_intentions: fakerIntentions,
            vices: fakerVices,
            job: fakerJob,
            education: fakerEducation,
            prompts: pickedPrompts,
          });
          generatedBio = r.bio;
          generatedAnswers = r.prompt_answers.map((a) => ({
            promptId: a.prompt_id,
            answer: a.answer,
          }));
        } catch (e) {
          // Fallback to faker-only — bio + lorem prompt answers.
          generatedBio = faker.lorem.sentences({ min: 2, max: 3 });
          generatedAnswers = pickedPrompts.map((p) => ({
            promptId: p.id,
            answer: faker.lorem.sentence({ min: 6, max: 14 }),
          }));
          console.warn('[admin] DeepSeek unavailable, used faker fallback', e);
        }
      } else {
        generatedBio = faker.lorem.sentences({ min: 2, max: 3 });
      }
      setBio(generatedBio);
      setPromptAnswers(generatedAnswers);

      // 4. Location — pick a faker city + ISO country, resolve via geocoder.
      const country = faker.location.countryCode('alpha-2');
      const city = faker.location.city();
      try {
        const resolved = await forwardSearch({
          countryCode: country,
          city,
        });
        if (resolved) {
          setLocation({
            latitude: resolved.latitude,
            longitude: resolved.longitude,
            city: resolved.city,
            country: resolved.country,
            countryCode: resolved.countryCode ?? country,
            source: 'auto',
          });
        }
      } catch (e) {
        console.warn('[admin] geocoder failed', e);
      }
    } finally {
      setGenerating(false);
    }
  }, [generating, gender, lookingFor, promptsByCategory, forwardSearch]);

  const save = useCallback(async () => {
    if (!userId || saving) return;
    setSaving(true);
    setErr(null);
    try {
      const patch: ProfilePatch = {
        dob: dob.trim() || undefined,
        bio: bio || undefined,
        gender: gender || undefined,
        lookingFor,
        education: education || undefined,
        educationLevel: educationLevel || undefined,
        job: job || undefined,
        hometown: hometown || undefined,
        religion: religion || undefined,
        politics: politics || undefined,
        languages,
        vices,
        datingIntentions,
        promptAnswers: promptAnswers.filter((p) => p.answer.trim().length > 0),
        themeSong,
        location,
      };
      await builderAPI.patchProfile(userId, patch);
      // Only publish if DOB is set — the backend would refuse anyway
      // (NO_DOB), but gating client-side keeps the error surface clean.
      if (autoPublish && dob.trim().length > 0) {
        const r = await builderAPI.publish(userId);
        setPublished(r);
      }
    } catch (e) {
      setErr(friendlyErrorOrCode(e));
    } finally {
      setSaving(false);
    }
  }, [
    userId,
    saving,
    dob,
    bio,
    gender,
    lookingFor,
    education,
    educationLevel,
    job,
    hometown,
    religion,
    politics,
    languages,
    vices,
    datingIntentions,
    promptAnswers,
    themeSong,
    location,
    autoPublish,
  ]);

  // ─── Render ────────────────────────────────────────────────────────────────

  const photosReady = photos.filter((p) => p.state === 'done').length;
  const canSave = userId !== null && photosReady >= 1;
  const canPublish = canSave && dob.trim().length > 0;
  const effectiveAutoPublish = autoPublish && canPublish;

  return (
    <div style={{ maxWidth: 880, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 24, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h2 className="serif" style={{ fontSize: 28, fontWeight: 500, fontStyle: 'italic', margin: 0 }}>
            Create user
          </h2>
          <p style={{ color: 'var(--ink-3)', fontSize: 13, marginTop: 6 }}>
            Auth shell + full profile + auto-publish. Photos bypass NSFW; email is
            force-verified so publish gate passes.
          </p>
        </div>
        <Link to="/dashboard/users" style={{ color: 'var(--ink-3)', fontSize: 13 }}>
          ← Back to users
        </Link>
      </div>

      {err && (
        <div style={errBox}>{err}</div>
      )}
      {published && (
        <div style={okBox}>
          Profile published! Slug: <code>{published.slug}</code>
          {' · '}
          <a href={published.public_url} target="_blank" rel="noreferrer">
            {published.public_url}
          </a>
          {' · '}
          <button
            type="button"
            style={linkBtn}
            onClick={() => {
              navigator.clipboard.writeText(published.public_url).catch(() => null);
            }}
          >
            Copy URL
          </button>
        </div>
      )}

      {/* ── Section 1: auth ── */}
      <Section title="1. Auth" enabled>
        <Grid cols={3}>
          <Field label="Email">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tester@example.com"
              disabled={!!userId}
              style={input}
            />
          </Field>
          <Field
            label="Password"
            hint={
              !userId && (
                <button
                  type="button"
                  style={hintBtn}
                  onClick={() => setPassword(randomPassword())}
                >
                  generate
                </button>
              )
            }
          >
            <input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="≥8 chars + digit"
              disabled={!!userId}
              style={{ ...input, fontFamily: 'var(--font-mono)' }}
            />
          </Field>
          <Field label="Display name">
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Optional"
              disabled={!!userId}
              style={input}
            />
          </Field>
        </Grid>
        {!userId ? (
          <button type="button" onClick={createUser} disabled={authBusy} style={primaryBtn}>
            {authBusy ? 'Creating…' : 'Create user'}
          </button>
        ) : (
          <div style={{ fontSize: 13, color: 'var(--ink-3)', overflowWrap: 'anywhere' }}>
            <strong>Created.</strong> User id: <code>{userId}</code>
          </div>
        )}
      </Section>

      {/* ── Section 2: identity ── */}
      <Section title="2. Identity" enabled={!!userId}>
        <Grid cols={2}>
          <Field label="Date of birth (required to publish)">
            <input
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              max={new Date().toISOString().slice(0, 10)}
              style={input}
            />
          </Field>
          <Field label="Gender">
            <ChipRow
              options={[
                { value: 'woman', label: 'Woman' },
                { value: 'man', label: 'Man' },
              ]}
              value={gender}
              onChange={(v) => setGender(v as Gender)}
            />
          </Field>
          <Field label="Looking for (multi)">
            <ChipRow
              multi
              options={[
                { value: 'women', label: 'Women' },
                { value: 'men', label: 'Men' },
                { value: 'everyone', label: 'Everyone' },
              ]}
              value={lookingFor}
              onChange={(v) => setLookingFor(v as LookingFor[])}
            />
          </Field>
        </Grid>
      </Section>

      {/* ── Section 3: generate ── */}
      <Section title="3. Generate full profile" enabled={!!userId && !!gender}>
        <p style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 0 }}>
          Fills bio + 3 prompt answers (DeepSeek), plus structured fields and
          location (faker + geocoder). Non-destructive — edit anything before
          saving. Theme song still picked manually below.
        </p>
        <button
          type="button"
          onClick={generateAll}
          disabled={generating || !gender}
          style={primaryBtn}
        >
          {generating ? 'Generating…' : 'Generate full profile'}
        </button>
      </Section>

      {/* ── Section 4: photos ── */}
      <Section title="4. Photos" enabled={!!userId}>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          onChange={(e) => {
            onPhotosPicked(e.target.files);
            e.target.value = '';
          }}
          disabled={!userId}
          style={{ marginBottom: 12 }}
        />
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onPhotoDragEnd}
        >
          <SortableContext
            items={photos.map((p) => p.id)}
            strategy={rectSortingStrategy}
          >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
              {photos.map((p, i) => (
                <SortablePhotoTile key={p.id} row={p} isLead={i === 0} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 8 }}>
          {photosReady} ready · drag to reorder · first card is the lead photo
        </div>
      </Section>

      {/* ── Section 5: videos ── */}
      <Section title="5. Videos" enabled={!!userId}>
        <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 0 }}>
          Each file is transcoded to H.264 in-browser (≤90s, ≤500MB) and uploaded
          with a poster frame. ~20-30s per ~90s clip.
        </p>
        <input
          type="file"
          accept="video/mp4,video/quicktime"
          multiple
          onChange={(e) => {
            onVideosPicked(e.target.files);
            e.target.value = '';
          }}
          disabled={!userId}
          style={{ marginBottom: 12 }}
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
          {videos.map((v) => (
            <VideoTile key={v.id} row={v} />
          ))}
        </div>
      </Section>

      {/* ── Section 6: text fields ── */}
      <Section title="6. Profile" enabled={!!userId}>
        <Field label="Bio (≤500 chars)">
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value.slice(0, 500))}
            rows={3}
            style={input}
          />
        </Field>
        <Grid cols={2}>
          <Field label="Job">
            <input value={job} onChange={(e) => setJob(e.target.value)} style={input} />
          </Field>
          <Field label="Hometown">
            <input value={hometown} onChange={(e) => setHometown(e.target.value)} style={input} />
          </Field>
        </Grid>
        <Grid cols={2}>
          <Field label="Education">
            <input value={education} onChange={(e) => setEducation(e.target.value)} style={input} />
          </Field>
          <Field label="Education level">
            <select
              value={educationLevel}
              onChange={(e) => setEducationLevel(e.target.value as EducationLevel | '')}
              style={input}
            >
              <option value="">—</option>
              {EDUCATION_LEVELS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
        </Grid>
        <Grid cols={2}>
          <Field label="Religion">
            <select value={religion} onChange={(e) => setReligion(e.target.value)} style={input}>
              <option value="">—</option>
              {RELIGIONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </Field>
          <Field label="Politics">
            <select
              value={politics}
              onChange={(e) => setPolitics(e.target.value as Politics | '')}
              style={input}
            >
              <option value="">—</option>
              {POLITICS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>
        </Grid>
        <Field label="Languages">
          <ChipInput
            values={languages}
            onChange={setLanguages}
            input={languageInput}
            setInput={setLanguageInput}
            placeholder="Add language + Enter"
          />
        </Field>
        <Field label="Vices">
          <ChipInput
            values={vices}
            onChange={setVices}
            input={viceInput}
            setInput={setViceInput}
            placeholder="drinks, smokes, etc."
            presets={VICE_PRESETS}
          />
        </Field>
        <Field label="Dating intentions (multi)">
          <ChipRow
            multi
            options={DATING_INTENTIONS.map((d) => ({ value: d.value, label: d.label }))}
            value={datingIntentions}
            onChange={(v) => setDatingIntentions(v as DatingIntention[])}
          />
        </Field>

        <PromptAnswersEditor
          prompts={prompts ?? []}
          value={promptAnswers}
          onChange={setPromptAnswers}
        />

        <ThemeSongPicker value={themeSong} onChange={setThemeSong} searchTracks={searchTracks} />

        <LocationPicker value={location} onChange={setLocation} forwardSearch={forwardSearch} />
      </Section>

      {/* ── Section 7: submit ── */}
      <Section title="7. Save + publish" enabled={canSave}>
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 13,
            opacity: canPublish ? 1 : 0.55,
          }}
          title={canPublish ? undefined : 'Set a date of birth in section 2 to enable publishing.'}
        >
          <input
            type="checkbox"
            checked={effectiveAutoPublish}
            disabled={!canPublish}
            onChange={(e) => setAutoPublish(e.target.checked)}
          />
          Auto-publish after save
        </label>
        <div style={{ marginTop: 12, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button type="button" disabled={!canSave || saving} onClick={save} style={primaryBtn}>
            {saving ? 'Saving…' : effectiveAutoPublish ? 'Save + publish' : 'Save profile'}
          </button>
          <button type="button" onClick={() => navigate('/dashboard/users')} style={secondaryBtn}>
            Done
          </button>
        </div>
        {!canSave && userId && (
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 8 }}>
            Upload at least 1 photo before saving.
          </div>
        )}
        {canSave && !canPublish && (
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 8 }}>
            Save is allowed without a date of birth, but publishing requires one — set it in section 2.
          </div>
        )}
      </Section>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────

function Section({
  title, children, enabled,
}: {
  title: string;
  children: React.ReactNode;
  enabled: boolean;
}) {
  return (
    <section
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 14,
        padding: 20,
        marginBottom: 16,
        opacity: enabled ? 1 : 0.45,
        pointerEvents: enabled ? 'auto' : 'none',
      }}
    >
      <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 14px', color: 'var(--ink)' }}>
        {title}
      </h3>
      {children}
    </section>
  );
}

function Grid({ children, cols }: { children: React.ReactNode; cols: number }) {
  return (
    <div
      className="stack-mobile"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gap: 12,
        marginBottom: 12,
      }}
    >
      {children}
    </div>
  );
}

function Field({
  label, children, hint,
}: { label: string; children: React.ReactNode; hint?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <label style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: 0.5, textTransform: 'uppercase' }}>
          {label}
        </label>
        {hint}
      </div>
      {children}
    </div>
  );
}

function ChipRow<T extends string>({
  options, value, onChange, multi,
}: {
  options: Array<{ value: T; label: string }>;
  value: T | T[] | '';
  onChange: (v: T | T[]) => void;
  multi?: boolean;
}) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {options.map((o) => {
        const active = multi
          ? Array.isArray(value) && value.includes(o.value)
          : value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => {
              if (multi) {
                const set = new Set(Array.isArray(value) ? value : []);
                if (set.has(o.value)) set.delete(o.value);
                else set.add(o.value);
                onChange(Array.from(set) as T[]);
              } else {
                onChange(o.value);
              }
            }}
            style={{
              padding: '6px 12px',
              borderRadius: 100,
              border: active ? '1px solid var(--coral)' : '1px solid var(--line)',
              background: active ? 'rgba(232,154,110,0.12)' : 'var(--bg)',
              color: active ? 'var(--coral)' : 'var(--ink)',
              fontSize: 13,
              cursor: 'pointer',
              fontWeight: active ? 600 : 500,
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function ChipInput({
  values, onChange, input, setInput, placeholder, presets,
}: {
  values: string[];
  onChange: (next: string[]) => void;
  input: string;
  setInput: (s: string) => void;
  placeholder: string;
  presets?: string[];
}) {
  const add = (raw: string) => {
    const v = raw.trim();
    if (!v || values.includes(v)) return;
    onChange([...values, v]);
    setInput('');
  };
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
        {values.map((v) => (
          <span key={v} style={chipStyle}>
            {v}
            <button
              type="button"
              onClick={() => onChange(values.filter((x) => x !== v))}
              style={chipX}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <input
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add(input);
          }
        }}
        placeholder={placeholder}
        style={inputStyle()}
      />
      {presets && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
          {presets
            .filter((p) => !values.includes(p))
            .map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => onChange([...values, p])}
                style={{
                  fontSize: 11,
                  padding: '3px 8px',
                  borderRadius: 100,
                  border: '1px dashed var(--line)',
                  background: 'transparent',
                  cursor: 'pointer',
                  color: 'var(--ink-3)',
                }}
              >
                + {p}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

function PhotoTile({
  row, isLead, dragHandleRef, dragHandleListeners, dragHandleAttributes,
}: {
  row: { id: string; file: File; state: string; pct?: number; error?: string };
  isLead?: boolean;
  dragHandleRef?: (n: HTMLElement | null) => void;
  dragHandleListeners?: Record<string, unknown>;
  dragHandleAttributes?: Record<string, unknown>;
}) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    const url = URL.createObjectURL(row.file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [row.file]);
  return (
    <div style={tileStyle}>
      <div
        ref={dragHandleRef}
        {...(dragHandleAttributes ?? {})}
        {...(dragHandleListeners ?? {})}
        style={{ position: 'relative', cursor: dragHandleRef ? 'grab' : 'default', touchAction: 'none' }}
      >
        {src && <img src={src} alt="" style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block', pointerEvents: 'none' }} />}
        {isLead && row.state === 'done' && (
          <span
            style={{
              position: 'absolute',
              top: 6,
              left: 6,
              background: 'var(--coral)',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.5,
              padding: '3px 8px',
              borderRadius: 100,
              textTransform: 'uppercase',
            }}
          >
            Lead
          </span>
        )}
      </div>
      <div style={tileLabel}>
        {row.state === 'done' && <span style={{ color: '#2e7d32' }}>✓ done</span>}
        {row.state === 'uploading' && <span>uploading {row.pct ?? 0}%</span>}
        {row.state === 'error' && <span style={{ color: '#a8331f' }}>err: {row.error}</span>}
      </div>
    </div>
  );
}

function SortablePhotoTile({
  row, isLead,
}: {
  row: { id: string; file: File; state: string; pct?: number; error?: string };
  isLead: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: row.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };
  return (
    <div ref={setNodeRef} style={style}>
      <PhotoTile
        row={row}
        isLead={isLead}
        dragHandleRef={setNodeRef as unknown as (n: HTMLElement | null) => void}
        dragHandleAttributes={attributes as unknown as Record<string, unknown>}
        dragHandleListeners={listeners as unknown as Record<string, unknown>}
      />
    </div>
  );
}

function VideoTile({ row }: { row: { id: string; file: File; stage: string; pct?: number; error?: string } }) {
  const label =
    row.stage === 'done' ? '✓ ready'
      : row.stage === 'error' ? `err: ${row.error}`
      : row.stage === 'probe' ? 'reading…'
      : row.stage === 'transcode' ? `transcoding ${row.pct ?? 0}%`
      : row.stage === 'upload' ? `uploading ${row.pct ?? 0}%`
      : row.stage === 'finalize' ? 'finalizing'
      : row.stage;
  return (
    <div style={tileStyle}>
      <div style={{ height: 100, background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-3)' }}>
        {row.file.name.slice(0, 24)}
      </div>
      <div style={{ ...tileLabel, color: row.stage === 'error' ? '#a8331f' : 'var(--ink)' }}>
        {label}
      </div>
    </div>
  );
}

function PromptAnswersEditor({
  prompts, value, onChange,
}: {
  prompts: Array<{ id: string; category: string; text: string }>;
  value: Array<{ promptId: string; answer: string }>;
  onChange: (next: Array<{ promptId: string; answer: string }>) => void;
}) {
  const ensureSlot = (idx: number, patch: Partial<{ promptId: string; answer: string }>) => {
    const next = [...value];
    while (next.length <= idx) next.push({ promptId: '', answer: '' });
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  return (
    <Field label="Prompt answers (up to 3)">
      {[0, 1, 2].map((i) => {
        const slot = value[i] ?? { promptId: '', answer: '' };
        return (
          <div key={i} className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <select
              value={slot.promptId}
              onChange={(e) => ensureSlot(i, { promptId: e.target.value })}
              style={inputStyle()}
            >
              <option value="">— pick prompt —</option>
              {prompts.map((p) => (
                <option key={p.id} value={p.id}>{p.text}</option>
              ))}
            </select>
            <input
              type="text"
              value={slot.answer}
              onChange={(e) => ensureSlot(i, { answer: e.target.value.slice(0, 300) })}
              placeholder="Answer…"
              style={inputStyle()}
            />
          </div>
        );
      })}
    </Field>
  );
}

function ThemeSongPicker({
  value, onChange, searchTracks,
}: {
  value: ThemeSong | null;
  onChange: (next: ThemeSong | null) => void;
  searchTracks: (args: { query: string; limit?: number }) => Promise<{ results: Array<{
    trackId: string; trackName: string; artistName: string; albumArtUrl: string | null;
    previewUrl: string | null; durationMs: number; spotifyUrl: string;
  }> }>;
}) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Awaited<ReturnType<typeof searchTracks>>['results']>([]);
  const [busy, setBusy] = useState(false);
  const doSearch = useCallback(async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const r = await searchTracks({ query: q.trim() });
      setResults(r.results);
    } finally {
      setBusy(false);
    }
  }, [q, searchTracks]);

  return (
    <Field label="Theme song (Deezer)">
      <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              doSearch();
            }
          }}
          placeholder="artist or track…"
          style={{ ...inputStyle(), flex: 1, minWidth: 0 }}
        />
        <button type="button" onClick={doSearch} disabled={busy || !q.trim()} style={secondaryBtn}>
          {busy ? 'Searching…' : 'Search'}
        </button>
      </div>
      {value && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 8, border: '1px solid var(--line)', borderRadius: 8, marginBottom: 8 }}>
          {value.albumArtUrl && (
            <img src={value.albumArtUrl} alt="" style={{ width: 36, height: 36, borderRadius: 4 }} />
          )}
          <div style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
            <div style={{ fontWeight: 600 }}>{value.trackName}</div>
            <div style={{ color: 'var(--ink-3)' }}>{value.artistName}</div>
          </div>
          <button type="button" onClick={() => onChange(null)} style={linkBtn}>
            Clear
          </button>
        </div>
      )}
      {results.length > 0 && (
        <div style={{ maxHeight: 240, overflow: 'auto', border: '1px solid var(--line)', borderRadius: 8 }}>
          {results.map((r) => (
            <button
              key={r.trackId}
              type="button"
              onClick={() => {
                onChange({
                  trackId: r.trackId,
                  trackName: r.trackName,
                  artistName: r.artistName,
                  albumArtUrl: r.albumArtUrl ?? undefined,
                  durationMs: r.durationMs,
                  spotifyUrl: r.spotifyUrl,
                });
                setResults([]);
                setQ('');
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                padding: 8,
                background: 'transparent',
                border: 'none',
                borderBottom: '1px solid var(--line)',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              {r.albumArtUrl && (
                <img src={r.albumArtUrl} alt="" style={{ width: 32, height: 32, borderRadius: 4 }} />
              )}
              <div style={{ flex: 1, fontSize: 12 }}>
                <div style={{ fontWeight: 600, color: 'var(--ink)' }}>{r.trackName}</div>
                <div style={{ color: 'var(--ink-3)' }}>{r.artistName}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </Field>
  );
}

function LocationPicker({
  value, onChange, forwardSearch,
}: {
  value: Location | null;
  onChange: (v: Location | null) => void;
  forwardSearch: (args: {
    countryCode: string; city?: string; postalCode?: string; neighborhood?: string;
  }) => Promise<{
    latitude: number; longitude: number; city: string; country: string; countryCode: string | null;
  } | null>;
}) {
  const [country, setCountry] = useState(value?.countryCode ?? 'US');
  const [city, setCity] = useState(value?.city ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const resolve = useCallback(async () => {
    if (!country.trim() || !city.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await forwardSearch({ countryCode: country.trim(), city: city.trim() });
      if (!r) {
        setErr('No match — try a different city or country code.');
        return;
      }
      onChange({
        latitude: r.latitude,
        longitude: r.longitude,
        city: r.city,
        country: r.country,
        countryCode: r.countryCode ?? country.trim(),
        source: 'manual',
      });
    } catch (e) {
      setErr(friendlyErrorOrCode(e));
    } finally {
      setBusy(false);
    }
  }, [city, country, forwardSearch, onChange]);

  return (
    <Field label="Location">
      {/* Flex rather than a fixed grid so the buttons drop to a second line on a phone. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <input
          type="text"
          value={country}
          onChange={(e) => setCountry(e.target.value.toUpperCase().slice(0, 2))}
          placeholder="ISO"
          style={{ ...inputStyle(), flex: '0 0 90px' }}
        />
        <input
          type="text"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="City"
          style={{ ...inputStyle(), flex: '1 1 160px', minWidth: 0 }}
        />
        <button type="button" onClick={resolve} disabled={busy} style={secondaryBtn}>
          {busy ? 'Resolving…' : 'Resolve'}
        </button>
        <button type="button" onClick={() => onChange(null)} style={linkBtn}>
          Clear
        </button>
      </div>
      {value && (
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6 }}>
          {value.city}, {value.country} ({value.latitude.toFixed(4)}, {value.longitude.toFixed(4)})
        </div>
      )}
      {err && <div style={{ fontSize: 12, color: '#a8331f', marginTop: 6 }}>{err}</div>}
    </Field>
  );
}

// ─── Styles + helpers ────────────────────────────────────────────────────

function inputStyle(): React.CSSProperties {
  return {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid var(--line)',
    borderRadius: 8,
    background: 'var(--bg)',
    fontSize: 13,
    color: 'var(--ink)',
    fontFamily: 'var(--font-ui)',
  };
}

const input = inputStyle();

const primaryBtn: React.CSSProperties = {
  padding: '10px 18px',
  background: 'var(--coral)',
  color: '#fff',
  border: 'none',
  borderRadius: 100,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const secondaryBtn: React.CSSProperties = {
  padding: '8px 14px',
  background: 'var(--bg)',
  color: 'var(--ink)',
  border: '1px solid var(--line)',
  borderRadius: 100,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const linkBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--coral)',
  fontSize: 12,
  cursor: 'pointer',
  fontWeight: 600,
};

const hintBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--coral)',
  fontSize: 10,
  fontWeight: 600,
  cursor: 'pointer',
  padding: 0,
};

const chipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '3px 10px',
  background: 'rgba(232,154,110,0.12)',
  color: 'var(--coral)',
  borderRadius: 100,
  fontSize: 12,
  fontWeight: 600,
};

const chipX: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--coral)',
  cursor: 'pointer',
  fontSize: 14,
  lineHeight: 1,
  padding: 0,
};

const tileStyle: React.CSSProperties = {
  border: '1px solid var(--line)',
  borderRadius: 8,
  overflow: 'hidden',
  background: 'var(--surface)',
};

const tileLabel: React.CSSProperties = {
  padding: '6px 8px',
  fontSize: 11,
  color: 'var(--ink-3)',
};

const errBox: React.CSSProperties = {
  background: 'rgba(208,64,48,0.08)',
  color: '#a8331f',
  padding: '10px 14px',
  borderRadius: 12,
  fontSize: 13,
  marginBottom: 16,
};

const okBox: React.CSSProperties = {
  background: 'rgba(56,142,60,0.10)',
  color: '#2e7d32',
  padding: '10px 14px',
  borderRadius: 12,
  fontSize: 13,
  marginBottom: 16,
  // The published URL is one long unbroken token.
  overflowWrap: 'anywhere',
};

function randomPassword(): string {
  const alpha = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz';
  const out: string[] = [];
  for (let i = 0; i < 11; i++) out.push(alpha[Math.floor(Math.random() * alpha.length)]);
  out.push(String(Math.floor(Math.random() * 10)));
  return out.sort(() => Math.random() - 0.5).join('');
}

function friendlyErrorOrCode(e: unknown): string {
  if (e instanceof BuilderError) {
    return e.message || e.code;
  }
  return friendlyError(e, 'createUser');
}
